import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Table,
  Descriptions,
  Tag,
  Typography,
  message,
} from "antd";
import {
  Calculator,
  DollarSign,
  Package,
  Truck,
  Users,
  Clock,
  Info,
} from "lucide-react";
import React, { useState } from "react";
import {
  CollectionDeliveryCalculation,
  ServiceCalculationResult,
  SiteServiceCalculation,
  WarehouseCalculation,
  useCalculateCollectionDelivery,
  useCalculateSiteService,
  useCalculateWarehouse,
  useEstimatePackingCost,
} from "~/services/service-metrics";
import { useGetStaffRoles } from "~/services/common";
import { useGetAllVessel } from "~/services/operation";
import { useGetClients } from "~/services/client/client.queries";
import { addOnServicesApi } from "~/services/add-on-services";
import { useQuery } from "@tanstack/react-query";

const { Text, Title } = Typography;

export const ServiceCalculatorTab: React.FC = () => {
  const [activeCalculator, setActiveCalculator] = useState<string>(
    "collection_delivery"
  );
  const [result, setResult] = useState<ServiceCalculationResult | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  const [collectionForm] = Form.useForm();
  const [siteForm] = Form.useForm();
  const [warehouseForm] = Form.useForm();
  const [packingForm] = Form.useForm();

  const { data: staffRoles } = useGetStaffRoles();
  const { data: vessels } = useGetAllVessel();
  const { data: clients } = useGetClients();
  const { data: addOnServices } = useQuery({
    queryKey: ['add-on-services'],
    queryFn: () => addOnServicesApi.getAll(),
  });

  const collectionMutation = useCalculateCollectionDelivery();
  const siteMutation = useCalculateSiteService();
  const warehouseMutation = useCalculateWarehouse();
  const packingMutation = useEstimatePackingCost();

  // Get selected client for shipping addresses
  const selectedClient = clients?.items.find(c => c.id === selectedClientId);

  const handleCollectionCalculation = async (values: any) => {
    try {
      // Get vessel data
      const selectedVessel = vessels?.items.find(v => v.id === values.vesselId);
      if (!selectedVessel) {
        message.error("Please select a valid vessel");
        return;
      }

      // Get client and shipping address data
      const selectedClient = clients?.items.find(c => c.id === values.clientId);
      const selectedAddress = selectedClient?.shippingAddresses?.find(a => a.id === values.shippingAddressId);
      
      const vesselRate = selectedVessel.hourlyRate || 90;
      const travelTime = selectedAddress?.travelTime ? selectedAddress.travelTime / 60 : 0.5; // Convert minutes to hours
      const siteTime = values.siteTime || 0.75;

      // Calculate staff costs from selected staff
      const staffCosts = values.staffAssignments?.map((assignment: any) => ({
        staffRoleId: assignment.roleId,
        quantity: assignment.quantity,
      })) || [];

      const data: CollectionDeliveryCalculation = {
        vesselRate,
        staffCosts,
        travelTime,
        siteTime,
      };

      const response = await collectionMutation.mutateAsync({ data });

      // Enhance the result with input values for reference
      const enhancedResult = {
        ...response.data,
        calculationDetails: {
          ...response.data.calculationDetails,
          vesselName: selectedVessel.name,
          clientName: selectedClient?.companyName,
          addressName: selectedAddress?.addressName,
          vesselRate,
          travelTime,
          siteTime,
        },
      };

      setResult(enhancedResult);
    } catch (error) {
      console.error("Calculation failed:", error);
      message.error("Failed to calculate collection & delivery cost");
    }
  };

  const handleSiteCalculation = async (values: any) => {
    try {
      // Calculate staff costs from selected staff
      const staffCosts = values.staffAssignments?.map((assignment: any) => ({
        staffRoleId: assignment.roleId,
        quantity: assignment.quantity,
        minimumHours: 2, // Default minimum hours for site service
      })) || [];

      const data: SiteServiceCalculation = {
        staffCosts,
        siteTime: values.siteTime,
      };

      const response = await siteMutation.mutateAsync({ data });
      setResult(response.data);
    } catch (error) {
      console.error("Calculation failed:", error);
      message.error("Failed to calculate site service cost");
    }
  };

  const handleWarehouseCalculation = async (values: any) => {
    try {
      // Get selected add-on service
      const selectedService = addOnServices?.find(s => s.id === values.serviceId);
      if (!selectedService) {
        message.error("Please select a valid warehouse service");
        return;
      }

      const data: WarehouseCalculation = {
        serviceRate: selectedService.rate || 0,
        minimumCharge: selectedService.minimumFee || 0,
        unitType: selectedService.unit === 'CU_FT' ? 'cubic_foot' : 'hourly',
        quantity: values.quantity,
      };

      const response = await warehouseMutation.mutateAsync({ data });

      // Enhance the result with service information
      const enhancedResult = {
        ...response.data,
        serviceName: `${selectedService.name} - Warehouse Service`,
        calculationDetails: {
          ...response.data.calculationDetails,
          serviceName: selectedService.name,
          serviceRef: selectedService.serviceRef,
          serviceRate: selectedService.rate,
          minimumCharge: selectedService.minimumFee,
          unitType: selectedService.unit,
          quantity: values.quantity,
        },
      };

      setResult(enhancedResult);
    } catch (error) {
      console.error("Calculation failed:", error);
      message.error("Failed to calculate warehouse service cost");
    }
  };

  const handlePackingCalculation = async (values: any) => {
    try {
      const response = await packingMutation.mutateAsync({
        length: values.length,
        width: values.width,
        height: values.height,
      });

      // Handle the packing metrics response and create a proper result
      const packingMetrics = response.data;
      if (packingMetrics && packingMetrics.length > 0) {
        // Calculate cubic feet
        const cubicFeet = (values.length * values.width * values.height) / 1728;

        // Find the best option (sorted by price in backend)
        const bestOption = packingMetrics[0];
        const baseRate =
          typeof bestOption.baseRate === "string"
            ? parseFloat(bestOption.baseRate)
            : Number(bestOption.baseRate) ||
              Number(bestOption.ratePerCubicFoot) ||
              0;
        const minimumCharge =
          typeof bestOption.minimumCharge === "string"
            ? parseFloat(bestOption.minimumCharge)
            : Number(bestOption.minimumCharge) || 0;

        const calculatedCost = baseRate * cubicFeet;
        const totalCost = Math.max(calculatedCost, minimumCharge);

        const result: ServiceCalculationResult = {
          serviceName: `Packing - ${bestOption.packingType}`,
          totalCost,
          calculationDetails: {
            dimensions: `${values.length}×${values.width}×${values.height} inches`,
            cubicFeet: cubicFeet.toFixed(2),
            packingType: bestOption.packingType,
            baseRate,
            minimumCharge,
            calculatedCost,
            totalCost,
            formula: `${baseRate}/ft³ × ${cubicFeet.toFixed(
              2
            )}ft³ = $${calculatedCost.toFixed(
              2
            )} (min: $${minimumCharge.toFixed(2)})`,
            alternativeOptions: packingMetrics.map((metric) => ({
              packingType: metric.packingType,
              baseRate:
                typeof metric.baseRate === "string"
                  ? parseFloat(metric.baseRate)
                  : Number(metric.baseRate) ||
                    Number(metric.ratePerCubicFoot) ||
                    0,
              minimumCharge:
                typeof metric.minimumCharge === "string"
                  ? parseFloat(metric.minimumCharge)
                  : Number(metric.minimumCharge) || 0,
            })),
          },
        };

        setResult(result);
      } else {
        console.error("No suitable packing options found");
      }
    } catch (error) {
      console.error("Calculation failed:", error);
    }
  };

  const calculatorTypes = [
    {
      key: "collection_delivery",
      title: "Collection & Delivery",
      icon: <Truck size={20} />,
      description: "Real vessel rates + client travel times",
    },
    {
      key: "site_service",
      title: "Site Service",
      icon: <Users size={20} />,
      description: "Staff rates with minimums from backend",
    },
    {
      key: "warehouse",
      title: "Warehouse Service",
      icon: <Package size={20} />,
      description: "Add-on services from operations setup",
    },
    {
      key: "packing",
      title: "Packing Estimation",
      icon: <Calculator size={20} />,
      description: "Live packing metrics (CB Taco, Slipcase, etc.)",
    },
  ];

  const renderCollectionForm = () => (
    <Form
      form={collectionForm}
      layout="vertical"
      onFinish={handleCollectionCalculation}
    >
      <Alert
        message="Collection & Delivery Estimator"
        description="This estimator connects to real vessel rates from your backend setup and travel times from client shipping addresses."
        type="info"
        className="mb-4"
      />

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="vesselId"
            label="Vessel"
            rules={[{ required: true, message: "Please select a vessel" }]}
          >
            <Select
              placeholder="Select vessel"
              options={vessels?.items.map((vessel) => ({
                label: `${vessel.name} - $${vessel.hourlyRate}/hour`,
                value: vessel.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="clientId"
            label="Client"
            rules={[{ required: true, message: "Please select a client" }]}
          >
            <Select
              placeholder="Select client"
              onChange={setSelectedClientId}
              options={clients?.items.map((client) => ({
                label: client.companyName,
                value: client.id,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="shippingAddressId"
            label="Shipping Address"
            rules={[{ required: true, message: "Please select a shipping address" }]}
          >
            <Select
              placeholder="Select shipping address"
              disabled={!selectedClientId}
              options={selectedClient?.shippingAddresses?.map((address) => ({
                label: `${address.addressName} (${address.travelTime ? `${address.travelTime} min travel` : 'No travel time set'})`,
                value: address.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="siteTime"
            label="Site Duration"
            rules={[{ required: true, message: "Please enter site time" }]}
          >
            <InputNumber
              min={0}
              step={0.25}
              precision={2}
              className="w-full"
              placeholder="0.75"
              addonAfter="hours"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="staffAssignments"
        label="Staff Assignments"
        rules={[
          { required: true, message: "Please assign staff" },
        ]}
      >
        <Select
          mode="multiple"
          placeholder="Select staff roles"
          options={staffRoles?.items?.map((role) => ({
            label: `${role.name} - $${role.hourlyRate}/hour`,
            value: role.id,
          }))}
        />
      </Form.Item>

      <Button
        type="primary"
        htmlType="submit"
        icon={<Calculator size={16} />}
        size="large"
        block
      >
        Calculate Collection & Delivery Cost
      </Button>
    </Form>
  );

  const renderSiteForm = () => (
    <Form form={siteForm} layout="vertical" onFinish={handleSiteCalculation}>
      <Alert
        message="Site Service Estimator"
        description="Minimum $300 per art handler for first 2 hours, then usual hourly rate. This connects to real staff rates from backend setup."
        type="info"
        className="mb-4"
      />

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="siteTime"
            label="Site Duration"
            rules={[{ required: true, message: "Please enter site time" }]}
          >
            <InputNumber
              min={0}
              step={0.25}
              precision={2}
              className="w-full"
              placeholder="3.0"
              addonAfter="hours"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="staffAssignments"
            label="Staff Assignments"
            rules={[
              { required: true, message: "Please assign staff" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select staff roles"
              options={staffRoles?.items?.map((role) => ({
                label: `${role.name} - $${role.hourlyRate}/hour`,
                value: role.id,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Button
        type="primary"
        htmlType="submit"
        icon={<Calculator size={16} />}
        size="large"
        block
      >
        Calculate Site Service Cost
      </Button>
    </Form>
  );

  const renderWarehouseForm = () => (
    <Form
      form={warehouseForm}
      layout="vertical"
      onFinish={handleWarehouseCalculation}
    >
      <Alert
        message="Warehouse Service Estimator"
        description="Connects to your add-on services backend setup. Rate per unit with minimum charge applied automatically."
        type="info"
        className="mb-4"
      />

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="serviceId"
            label="Warehouse Service"
            rules={[{ required: true, message: "Please select a service" }]}
          >
            <Select
              placeholder="Select warehouse service"
              options={addOnServices?.filter(service => service.type === 'WAREHOUSE' && service.isActive).map((service) => ({
                label: `${service.name} - $${service.rate}/${service.unit === 'CU_FT' ? 'cu ft' : 'hour'} (min $${service.minimumFee})`,
                value: service.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: "Please enter quantity" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              precision={1}
              className="w-full"
              placeholder="60"
            />
          </Form.Item>
        </Col>
      </Row>

      <Button
        type="primary"
        htmlType="submit"
        icon={<Calculator size={16} />}
        size="large"
        block
      >
        Calculate Warehouse Service Cost
      </Button>
    </Form>
  );

  const renderPackingForm = () => (
    <Form
      form={packingForm}
      layout="vertical"
      onFinish={handlePackingCalculation}
    >
      <Alert
        message="Packing Cost Estimator"
        description="Estimate packing costs based on artwork dimensions. Connects to backend packing metrics for CB Taco, Slipcase, Softpack, Shadowbox, Floating Shadow, Blanketwrapping, T-Frame, and more."
        type="info"
        className="mb-4"
      />

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="length"
            label="Length"
            rules={[{ required: true, message: "Please enter length" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              precision={1}
              className="w-full"
              placeholder="24.0"
              addonAfter="inches"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="width"
            label="Width"
            rules={[{ required: true, message: "Please enter width" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              precision={1}
              className="w-full"
              placeholder="36.0"
              addonAfter="inches"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="height"
            label="Height"
            rules={[{ required: true, message: "Please enter height" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              precision={1}
              className="w-full"
              placeholder="2.0"
              addonAfter="inches"
            />
          </Form.Item>
        </Col>
      </Row>

      <div className="bg-blue-50 p-3 rounded mb-4">
        <Text className="text-sm text-blue-700">
          <Info size={14} className="inline mr-1" />
          This will automatically find the best packing option from your backend metrics based on dimensions and cost.
        </Text>
      </div>

      <Button
        type="primary"
        htmlType="submit"
        icon={<Calculator size={16} />}
        size="large"
        block
      >
        Estimate Packing Cost
      </Button>
    </Form>
  );

  const renderCalculatorForm = () => {
    switch (activeCalculator) {
      case "collection_delivery":
        return renderCollectionForm();
      case "site_service":
        return renderSiteForm();
      case "warehouse":
        return renderWarehouseForm();
      case "packing":
        return renderPackingForm();
      default:
        return null;
    }
  };

  // Add this helper component for detailed calculation display
  const CalculationDetails: React.FC<{
    result: ServiceCalculationResult;
    calculatorType: string;
  }> = ({ result, calculatorType }) => {
    const details = result.calculationDetails;

    if (!details) return null;

    const renderCollectionDeliveryDetails = () => (
      <div className="space-y-4">
        {/* Data Source Information */}
        <Alert
          message="Data Sources Used"
          description={
            <div className="text-sm">
              <div>• Vessel Rate: {details.vesselName} from backend setup</div>
              <div>• Travel Time: {details.addressName} shipping address</div>
              <div>• Staff Rates: Backend position rates</div>
            </div>
          }
          type="success"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Formula */}
        <Alert
          message="Calculation Formula"
          description={details.formula}
          type="info"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Staff Costs Breakdown */}
        {details.staffCosts && details.staffCosts.length > 0 && (
          <div>
            <Title level={5}>Staff Costs Breakdown</Title>
            <Table
              size="small"
              dataSource={details.staffCosts}
              pagination={false}
              rowKey="staffRoleId"
              columns={[
                {
                  title: "Role",
                  dataIndex: "staffRoleName",
                  key: "staffRoleName",
                },
                {
                  title: "Hourly Rate",
                  dataIndex: "hourlyRate",
                  key: "hourlyRate",
                  render: (rate) => {
                    const numRate = typeof rate === 'string' ? parseFloat(rate) : Number(rate);
                    return `$${(numRate || 0).toFixed(2)}/hr`;
                  },
                },
                {
                  title: "Quantity",
                  dataIndex: "quantity",
                  key: "quantity",
                  render: (qty) => `${qty} people`,
                },
                {
                  title: "Total Cost",
                  dataIndex: "cost",
                  key: "cost",
                  render: (cost) => {
                    const numCost = typeof cost === 'string' ? parseFloat(cost) : Number(cost);
                    return (
                      <Text strong className="text-green-600">
                        ${(numCost || 0).toFixed(2)}
                      </Text>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        {/* Time & Hours */}
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Vessel Rate">
            <Tag color="purple">
              ${details.vesselRate ? details.vesselRate.toFixed(2) : "N/A"}/hour
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Total Hours">
            <Tag color="blue" icon={<Clock size={12} />}>
              {details.hoursWorked} hours
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Travel Time">
            <Tag color="cyan">
              {details.travelTime ? `${details.travelTime} hours` : "N/A"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Site Time">
            <Tag color="geekblue">
              {details.siteTime ? `${details.siteTime} hours` : "N/A"}
            </Tag>
          </Descriptions.Item>
          {details.minimumHours && (
            <Descriptions.Item label="Minimum Hours">
              <Tag color="orange">{details.minimumHours} hours</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Cost Breakdown Summary */}
        <div className="bg-blue-50 p-3 rounded">
          <Title level={5}>Cost Breakdown Summary</Title>
          <div className="space-y-2">
            <div className="flex justify-between">
              <Text>Vessel Cost:</Text>
              <Text strong>
                ${details.vesselRate ? details.vesselRate.toFixed(2) : "N/A"} ×{" "}
                {details.hoursWorked} hours =
                <span className="text-blue-600 ml-1">
                  $
                  {details.vesselRate && details.hoursWorked
                    ? (details.vesselRate * details.hoursWorked).toFixed(2)
                    : "N/A"}
                </span>
              </Text>
            </div>
            <div className="flex justify-between">
              <Text>Total Staff Cost:</Text>
              <Text strong className="text-green-600">
                $
                {details.staffCosts
                  ? details.staffCosts
                      .reduce((sum: number, staff: any) => {
                        const cost = typeof staff.cost === 'string' ? parseFloat(staff.cost) : Number(staff.cost);
                        return sum + (cost || 0);
                      }, 0)
                      .toFixed(2)
                  : "N/A"}
              </Text>
            </div>
            <Divider className="my-2" />
            <div className="flex justify-between">
              <Text strong>Grand Total:</Text>
              <Text strong className="text-green-600 text-lg">
                ${result.totalCost.toFixed(2)}
              </Text>
            </div>
          </div>
        </div>
      </div>
    );

    const renderSiteServiceDetails = () => (
      <div className="space-y-4">
        {/* Data Source Information */}
        <Alert
          message="Data Sources Used"
          description={
            <div className="text-sm">
              <div>• Staff Rates: Backend position rates</div>
              <div>• Minimum Hours: System configuration (2 hours)</div>
              <div>• Minimum Charge: $300 per art handler</div>
            </div>
          }
          type="success"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Formula */}
        <Alert
          message="Calculation Formula"
          description={details.formula}
          type="info"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Staff Costs Breakdown */}
        {details.staffCosts && details.staffCosts.length > 0 && (
          <div>
            <Title level={5}>Staff Costs Breakdown</Title>
            <Table
              size="small"
              dataSource={details.staffCosts}
              pagination={false}
              rowKey="staffRoleId"
              columns={[
                {
                  title: "Role",
                  dataIndex: "staffRoleName",
                  key: "staffRoleName",
                },
                {
                  title: "Hourly Rate",
                  dataIndex: "hourlyRate",
                  key: "hourlyRate",
                  render: (rate) => {
                    const numRate = typeof rate === 'string' ? parseFloat(rate) : Number(rate);
                    return `$${(numRate || 0).toFixed(2)}/hr`;
                  },
                },
                {
                  title: "Quantity",
                  dataIndex: "quantity",
                  key: "quantity",
                  render: (qty) => `${qty} people`,
                },
                {
                  title: "Total Cost",
                  dataIndex: "cost",
                  key: "cost",
                  render: (cost) => {
                    const numCost = typeof cost === 'string' ? parseFloat(cost) : Number(cost);
                    return (
                      <Text strong className="text-green-600">
                        ${(numCost || 0).toFixed(2)}
                      </Text>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        {/* Time Details */}
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Site Time">
            <Tag color="blue" icon={<Clock size={12} />}>
              {details.hoursWorked} hours
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Minimum Hours">
            <Tag color="orange">{details.minimumHours} hours minimum</Tag>
          </Descriptions.Item>
        </Descriptions>
      </div>
    );

    const renderWarehouseDetails = () => (
      <div className="space-y-4">
        {/* Data Source Information */}
        <Alert
          message="Data Sources Used"
          description={
            <div className="text-sm">
              <div>• Service: {details.serviceName} from add-on services setup</div>
              <div>• Rate: ${details.serviceRate}/unit from backend configuration</div>
              <div>• Minimum: ${details.minimumCharge} from service settings</div>
            </div>
          }
          type="success"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Formula */}
        <Alert
          message="Calculation Formula"
          description={details.formula}
          type="info"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Calculation Breakdown */}
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Service">
            {details.serviceName} ({details.serviceRef})
          </Descriptions.Item>
          <Descriptions.Item label="Service Rate">
            ${details.serviceRate ? details.serviceRate.toFixed(2) : "N/A"} per{" "}
            {details.unitType === 'CU_FT' ? 'cubic foot' : 'hour'}
          </Descriptions.Item>
          <Descriptions.Item label="Quantity">
            {details.quantity || "N/A"} {details.unitType === 'CU_FT' ? 'cubic feet' : 'hours'}
          </Descriptions.Item>
          <Descriptions.Item label="Calculated Cost">
            <Text>
              ${details.serviceRate ? details.serviceRate.toFixed(2) : "N/A"} ×{" "}
              {details.quantity || "N/A"} =
              <Text strong className="text-blue-600 ml-1">
                $
                {details.serviceRate && details.quantity
                  ? (details.serviceRate * details.quantity).toFixed(2)
                  : "N/A"}
              </Text>
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Minimum Charge">
            <Tag color="orange">
              $
              {details.minimumCharge ? details.minimumCharge.toFixed(2) : "N/A"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Applied Logic">
            <Text className="text-gray-600">
              {details.serviceRate && details.quantity && details.minimumCharge
                ? (details.serviceRate * details.quantity) >= details.minimumCharge
                  ? "Using calculated cost (exceeds minimum)"
                  : "Using minimum charge (calculated cost below minimum)"
                : "N/A"}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Final Cost">
            <Text strong className="text-green-600">
              ${result.totalCost.toFixed(2)}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </div>
    );

    const renderPackingDetails = () => (
      <div className="space-y-4">
        {/* Data Source Information */}
        <Alert
          message="Data Sources Used"
          description={
            <div className="text-sm">
              <div>• Packing Metrics: Live backend data</div>
              <div>• Selected Type: {details.packingType} (best option found)</div>
              <div>• Rate: ${details.baseRate}/ft³ from packing metrics</div>
              <div>• Minimum: ${details.minimumCharge} from packing setup</div>
            </div>
          }
          type="success"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Formula */}
        <Alert
          message="Calculation Formula"
          description={
            details.formula ||
            "Rate per cubic foot × Cubic feet (with minimum charge applied)"
          }
          type="info"
          showIcon
          icon={<Info size={16} />}
          className="mb-4"
        />

        {/* Dimensions & Volume */}
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Dimensions">
            <Tag color="blue">{details.dimensions}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Volume">
            <Tag color="green">{details.cubicFeet} cubic feet</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Packing Type">
            <Tag color="purple">{details.packingType}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Base Rate">
            ${details.baseRate?.toFixed(2)}/ft³
          </Descriptions.Item>
        </Descriptions>

        {/* Calculation Breakdown */}
        <div className="bg-blue-50 p-3 rounded">
          <Title level={5}>Cost Calculation</Title>
          <div className="space-y-2">
            <div>
              <Text>Base Rate: </Text>
              <Text strong>${details.baseRate?.toFixed(2)}/ft³</Text>
            </div>
            <div>
              <Text>Volume: </Text>
              <Text strong>{details.cubicFeet} ft³</Text>
            </div>
            <div>
              <Text>Calculated Cost: </Text>
              <Text strong className="text-blue-600">
                ${details.calculatedCost?.toFixed(2)}
              </Text>
            </div>
            <div>
              <Text>Minimum Charge: </Text>
              <Text strong className="text-orange-600">
                ${details.minimumCharge?.toFixed(2)}
              </Text>
            </div>
            <Divider className="my-2" />
            <div>
              <Text>Final Cost: </Text>
              <Text strong className="text-green-600 text-lg">
                ${details.totalCost?.toFixed(2)}
              </Text>
            </div>
          </div>
        </div>

        {/* Alternative Options */}
        {details.alternativeOptions &&
          details.alternativeOptions.length > 1 && (
            <div>
              <Title level={5}>Alternative Packing Options</Title>
              <Table
                size="small"
                dataSource={details.alternativeOptions}
                pagination={false}
                rowKey="packingType"
                columns={[
                  {
                    title: "Packing Type",
                    dataIndex: "packingType",
                    key: "packingType",
                  },
                  {
                    title: "Base Rate",
                    dataIndex: "baseRate",
                    key: "baseRate",
                    render: (rate) => {
                      const numRate = typeof rate === 'string' ? parseFloat(rate) : Number(rate);
                      return `$${(numRate || 0).toFixed(2)}/ft³`;
                    },
                  },
                  {
                    title: "Minimum Charge",
                    dataIndex: "minimumCharge",
                    key: "minimumCharge",
                    render: (charge) => {
                      const numCharge = typeof charge === 'string' ? parseFloat(charge) : Number(charge);
                      return `$${(numCharge || 0).toFixed(2)}`;
                    },
                  },
                  {
                    title: "Estimated Cost",
                    key: "estimatedCost",
                    render: (_, record: any) => {
                      const baseRate =
                        typeof record.baseRate === "string"
                          ? parseFloat(record.baseRate)
                          : record.baseRate;
                      const minimumCharge =
                        typeof record.minimumCharge === "string"
                          ? parseFloat(record.minimumCharge)
                          : record.minimumCharge;
                      const calculated =
                        baseRate * parseFloat(details.cubicFeet);
                      const final = Math.max(calculated, minimumCharge);
                      return (
                        <Text
                          strong
                          className={
                            final === details.totalCost
                              ? "text-green-600"
                              : "text-gray-600"
                          }
                        >
                          ${final.toFixed(2)}
                          {final === details.totalCost && (
                            <Tag color="green" className="ml-1">
                              Selected
                            </Tag>
                          )}
                        </Text>
                      );
                    },
                  },
                ]}
              />
            </div>
          )}
      </div>
    );

    // Render appropriate details based on calculator type
    switch (calculatorType) {
      case "collection_delivery":
        return renderCollectionDeliveryDetails();
      case "site_service":
        return renderSiteServiceDetails();
      case "warehouse":
        return renderWarehouseDetails();
      case "packing":
        return renderPackingDetails();
      default:
        // Fallback to JSON display for unknown types
        return (
          <div className="bg-gray-50 p-3 rounded text-sm">
            <pre className="whitespace-pre-wrap text-xs">
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Service Cost Estimator</h3>
        <p className="text-gray-500">
          Calculate costs using real data from your backend setup - vessel rates, client travel times, staff positions, add-on services, and packing metrics.
        </p>
      </div>

      {/* Calculator Type Selection */}
      <div className="grid grid-cols-4 gap-4">
        {calculatorTypes.map((type) => (
          <Card
            key={type.key}
            className={`cursor-pointer border-2 transition-all ${
              activeCalculator === type.key
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-blue-300"
            }`}
            onClick={() => setActiveCalculator(type.key)}
          >
            <div className="text-center">
              <div
                className={`mb-2 ${
                  activeCalculator === type.key
                    ? "text-blue-600"
                    : "text-gray-600"
                }`}
              >
                {type.icon}
              </div>
              <h4 className="font-medium text-sm">{type.title}</h4>
              <p className="text-xs text-gray-500 mt-1">{type.description}</p>
            </div>
          </Card>
        ))}
      </div>

      <Row gutter={24}>
        {/* Calculator Form */}
        <Col span={14}>
          <Card
            title={`${
              calculatorTypes.find((t) => t.key === activeCalculator)?.title
            } Estimator`}
          >
            {renderCalculatorForm()}
          </Card>
        </Col>

        {/* Results */}
        <Col span={10}>
          <Card
            title="Estimation Result"
            extra={
              result && <DollarSign size={16} className="text-green-600" />
            }
          >
            {result ? (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    ${(result.totalCost || 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {result.serviceName}
                  </div>
                </div>

                <Divider />

                <div className="space-y-2">
                  <h4 className="font-medium">Calculation Details:</h4>
                  <CalculationDetails
                    result={result}
                    calculatorType={activeCalculator}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Calculator size={48} className="mx-auto mb-4 opacity-50" />
                <p>
                  Select an estimator and fill in the details to see results from your backend data
                </p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
